import React, { useState, useEffect } from 'react';
import { SparringMode, SparringSession } from '../types';
import { createSparringSession, getSessionsByGoal } from '../firebase/sparring.service';
import { GoalIcon, SearchIcon, ChatIcon, TipIcon } from '../../../icons';

interface SparringModeSelectorProps {
  goalId: string;
  ownerId: string;
  onSessionCreated: (sessionId: string, mode: SparringMode) => void;
}

/**
 * SparringModeSelector - Keuze tussen gestructureerde of vrije sparring
 * 
 * Alleen zichtbaar bij committed goals.
 * Bij selectie wordt een SparringSession aangemaakt.
 * Toont ook bestaande sessies om te hervatten.
 */
const SparringModeSelector: React.FC<SparringModeSelectorProps> = ({
  goalId,
  ownerId,
  onSessionCreated,
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const [activeSessions, setActiveSessions] = useState<SparringSession[]>([]);
  const [closedSessions, setClosedSessions] = useState<SparringSession[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [showClosedSessions, setShowClosedSessions] = useState(false);

  // Load existing sessions on mount
  useEffect(() => {
    const loadSessions = async () => {
      setIsLoadingSessions(true);
      try {
        const sessions = await getSessionsByGoal(ownerId, goalId);
        // Separate active and closed sessions
        const active = sessions
          .filter(s => !s.closedAt)
          .sort((a, b) => b.createdAt - a.createdAt);
        const closed = sessions
          .filter(s => s.closedAt)
          .sort((a, b) => (b.closedAt || 0) - (a.closedAt || 0));
        
        setActiveSessions(active);
        setClosedSessions(closed);
        
        console.log('Found sessions:', { active: active.length, closed: closed.length, total: sessions.length });
      } catch (err) {
        console.error('Failed to load existing sessions:', err);
      } finally {
        setIsLoadingSessions(false);
      }
    };
    loadSessions();
  }, [ownerId, goalId]);

  const handleSelectMode = async (mode: SparringMode) => {
    if (isCreating) return;
    
    setIsCreating(true);
    try {
      const sessionId = await createSparringSession(ownerId, goalId, mode);
      onSessionCreated(sessionId, mode);
    } catch (err) {
      console.error('Failed to create sparring session:', err);
      setIsCreating(false);
    }
  };

  const handleResumeSession = (session: SparringSession) => {
    onSessionCreated(session.id, session.mode);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Active sessions - show first if available */}
      {!isLoadingSessions && activeSessions.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            📂 Lopende sessies
          </h3>
          <div className="space-y-2">
            {activeSessions.map(session => (
              <button
                key={session.id}
                onClick={() => handleResumeSession(session)}
                disabled={isCreating}
                className="w-full p-4 text-left border-2 border-orange-200 rounded-lg hover:border-orange-400 hover:bg-orange-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">
                      {session.mode === 'structured' ? <GoalIcon size={24} /> : session.mode === 'reflection' ? <SearchIcon size={24} /> : <ChatIcon size={24} />}
                    </span>
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {session.mode === 'structured' ? 'Gestructureerde' : session.mode === 'reflection' ? 'Reflectie' : 'Vrije'} sessie doorgaan
                      </h4>
                      <p className="text-sm text-gray-500">
                        {session.messages.length} berichten • {formatDate(
                          session.messages.length > 0 
                            ? session.messages[session.messages.length - 1].timestamp 
                            : session.createdAt
                        )}
                      </p>
                    </div>
                  </div>
                  <span className="text-orange-500 font-medium text-sm">
                    Hervatten →
                  </span>
                </div>
              </button>
            ))}
          </div>
          
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-500 mb-3">Of start een nieuwe sessie:</p>
          </div>
        </div>
      )}

      {/* Closed sessions - collapsible */}
      {!isLoadingSessions && closedSessions.length > 0 && (
        <div className="mb-6">
          <button
            onClick={() => setShowClosedSessions(!showClosedSessions)}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-2"
          >
            <span>{showClosedSessions ? '▼' : '▶'}</span>
            <span>📁 Afgesloten sessies ({closedSessions.length})</span>
          </button>
          
          {showClosedSessions && (
            <div className="space-y-2 pl-4">
              {closedSessions.map(session => (
                <button
                  key={session.id}
                  onClick={() => handleResumeSession(session)}
                  disabled={isCreating}
                  className="w-full p-3 text-left border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-xl opacity-60">
                        {session.mode === 'structured' ? <GoalIcon size={20} /> : session.mode === 'reflection' ? <SearchIcon size={20} /> : <ChatIcon size={20} />}
                      </span>
                      <div>
                        <h4 className="font-medium text-gray-600 text-sm">
                          {session.mode === 'structured' ? 'Gestructureerd' : session.mode === 'reflection' ? 'Reflectie' : 'Vrij'} (afgesloten)
                        </h4>
                        <p className="text-xs text-gray-400">
                          {session.messages.length} berichten • {formatDate(session.closedAt || session.createdAt)}
                        </p>
                      </div>
                    </div>
                    <span className="text-gray-400 text-xs">
                      Bekijken →
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {isLoadingSessions && (
        <div className="text-center text-gray-500 py-4 mb-4">
          Sessies laden...
        </div>
      )}

      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        {activeSessions.length > 0 ? '' : 'Start een sparringsessie'}
      </h3>
      
      {/* Info box explaining the modes */}
      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <span className="font-medium inline-flex items-center gap-1"><TipIcon size={14} /> Tip:</span> Alle sessie-types genereren mijlpalen uit je gesprek. 
          Je kunt meerdere sessies naast elkaar hebben en ze altijd hervatten.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Gestructureerde sparring */}
        <button
          onClick={() => handleSelectMode('structured')}
          disabled={isCreating}
          className={`
            p-4 text-left rounded-lg border-2 transition-colors
            ${isCreating 
              ? 'border-gray-200 bg-gray-50 cursor-not-allowed' 
              : 'border-gray-200 hover:border-orange-400 hover:bg-orange-50'
            }
          `}
        >
          <div className="flex items-start gap-3">
            <GoalIcon size={24} />
            <div>
              <h4 className="font-medium text-gray-900">
                Gestructureerde sparring
              </h4>
              <p className="text-sm text-gray-500 mt-1">
                7-stappen proces: van einddoel naar concrete weekacties
              </p>
              <p className="text-xs text-orange-600 mt-1 font-medium">
                Aanbevolen als eerste sessie
              </p>
            </div>
          </div>
        </button>

        {/* Vrije sparring */}
        <button
          onClick={() => handleSelectMode('free')}
          disabled={isCreating}
          className={`
            p-4 text-left rounded-lg border-2 transition-colors
            ${isCreating 
              ? 'border-gray-200 bg-gray-50 cursor-not-allowed' 
              : 'border-gray-200 hover:border-orange-400 hover:bg-orange-50'
            }
          `}
        >
          <div className="flex items-start gap-3">
            <ChatIcon size={24} />
            <div>
              <h4 className="font-medium text-gray-900">
                Vrije sparring
              </h4>
              <p className="text-sm text-gray-500 mt-1">
                Open gesprek zonder structuur, AI luistert en vraagt door
              </p>
              <p className="text-xs text-green-600 mt-1">
                ✓ Genereert ook mijlpalen
              </p>
            </div>
          </div>
        </button>

        {/* Reflectie vragen */}
        <button
          onClick={() => handleSelectMode('reflection')}
          disabled={isCreating}
          className={`
            p-4 text-left rounded-lg border-2 transition-colors
            ${isCreating 
              ? 'border-gray-200 bg-gray-50 cursor-not-allowed' 
              : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50'
            }
          `}
        >
          <div className="flex items-start gap-3">
            <SearchIcon size={24} />
            <div>
              <h4 className="font-medium text-gray-900">
                Reflectie: Zichtbaarheid
              </h4>
              <p className="text-sm text-gray-500 mt-1">
                Toets je aannames met externe feedback en marktvalidatie
              </p>
              <p className="text-xs text-green-600 mt-1">
                ✓ Genereert ook mijlpalen
              </p>
            </div>
          </div>
        </button>
      </div>

      {isCreating && (
        <div className="mt-4 text-center text-sm text-gray-500">
          Sessie starten...
        </div>
      )}
    </div>
  );
};

export default SparringModeSelector;
